import EditDynamicSwitchGroupDetails from 'components/EditDynamicSwitchGroupDetails';
import ToggleIconLinkButton from 'components/ToggleIconLinkButton';
import { Formik } from 'formik';
import Form from "react-bootstrap/Form";
import React, { useState, useEffect, useRef } from 'react';
import { Badge, Card, Col, Collapse, Row } from 'react-bootstrap';
import { showNotification } from 'services/NotificationService';
import { useDispatch, useSelector } from 'react-redux';
import withRouter from "utils/withRouter";
import EditExpandButtons from './EditExpandButtons';
import * as Yup from "yup";
import CAT_CONSTANTS from 'lookup/CatConstants';
import { updateDynamicSwitchGroup } from 'views/Cat/DynamicSwitchGroupDetails/DynamicSwitchGroupDetails.actions';
import { MdNoteAdd } from 'react-icons/md';
import cloneDeep from 'lodash/cloneDeep';
import { BooleanDeltaField, DescriptionDeltaField, getDeltaBoxClassName } from '../../commons/fields/DeltaFieldUtils';
import DrugDeltaIcon from './DrugDeltaIcon';

const DRUG_GROUP_SCHEMA = Yup.object().shape({
  dynamicSwitchGroupName: Yup.string()
    .max(255, CAT_CONSTANTS.fieldMaxLengthValidationPrefix + 255)
    .required(),
  dynamicSwitchGroupClassName: Yup.string()
    .max(255, CAT_CONSTANTS.fieldMaxLengthValidationPrefix + 255),
  dynamicSwitchGroupSubClassName: Yup.string()
    .max(255, CAT_CONSTANTS.fieldMaxLengthValidationPrefix + 255),
  additionalClassName: Yup.string()
    .max(255, CAT_CONSTANTS.fieldMaxLengthValidationPrefix + 255),
  pharmacistNotes: Yup.string()
    .max(1024, CAT_CONSTANTS.fieldMaxLengthValidationPrefix + 1024),
  isActive: Yup.boolean().required()
});

const DynamicSwitchGroupBox = (props) => {
  const dispatch = useDispatch();
  const history = props.history;  
  // Get state from Redux using useSelector
  const allowEditing = useSelector((state) => {
    return state.codexVersion?.isWorkingVersionEditable && 
           state.dynamicSwitchGroupDetails?.allowEditing && 
           !state.dynamicSwitchGroupDetails?.showDelta
      ? state.dynamicSwitchGroupDetails.allowEditing 
      : false;
  });
  const workingCodexVersionId = useSelector((state) => 
    state.codexVersion ? state.codexVersion.codexVersionId : null
  );
  const dynamicSwitchGroup = useSelector((state) => 
    state.dynamicSwitchGroupDetails ? state.dynamicSwitchGroupDetails.dynamicSwitchGroup : null
  );
  const oldDynamicSwitchGroup = useSelector((state) => 
    state.dynamicSwitchGroupDetails ? state.dynamicSwitchGroupDetails.oldDynamicSwitchGroup : null
  );
  const showDelta = useSelector((state) => 
    state.dynamicSwitchGroupDetails ? state.dynamicSwitchGroupDetails.showDelta : false
  );
  const dsgEditFormRef = useRef(null);
  const [showDetails, setShowDetails] = useState(false);
  const [groupIsDirty, setGroupIsDirty] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showNotesTextArea, setShowNotesTextArea] = useState(false);

  useEffect(() => {
    if (!allowEditing) {
      if (dsgEditFormRef.current) dsgEditFormRef.current.resetForm();
      setIsEditMode(false);
      setGroupIsDirty(false);
    }
  }, [allowEditing]);

  const toggleShowDetails = () => {
    setShowDetails(!showDetails);
  }

  const handleEnableEditMode = () => {
    setIsEditMode(true);
    setShowDetails(true);
    setGroupIsDirty(true);
  }

  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setShowDetails(false);
    setShowNotesTextArea(false);
    setGroupIsDirty(false);
  }

  const handleShowNotesTextArea = () => {
    setShowNotesTextArea(true);
  }

  const handleSaveEditMode = (updatedDynamicSwitchGroup, setSubmitting) => {
    setIsEditMode(false);
    setShowDetails(false);
    setShowNotesTextArea(false);
    setGroupIsDirty(false);
    if (updatedDynamicSwitchGroup.dynamicSwitchGroupName === dynamicSwitchGroup.dynamicSwitchGroupName &&
      updatedDynamicSwitchGroup.isActive === dynamicSwitchGroup.isActive &&
      (updatedDynamicSwitchGroup.pharmacistNotes === dynamicSwitchGroup.pharmacistNotes)) {
        showNotification({
          title: 'No Change Detected!',
          message: `You are trying to save without making any update.`,
          position: 'tr',
          autoDismiss: 5,
          type: "warning"
        });
      return;
    }
    let itemTypes = dynamicSwitchGroup.dynamicSwitchOptions ? new Set(dynamicSwitchGroup.dynamicSwitchOptions.map(x => x.itemType)) : new Set();
    let areAllDsoPresent = itemTypes.size >= 2 ? true : false;
    if(updatedDynamicSwitchGroup.isActive && !dynamicSwitchGroup.isActive && !areAllDsoPresent) {
      showNotification({
        title: 'Cannot mark this Group active!',
        message: `There should be atleast one switch and one target to make this group active`,
        position: 'tr',
        autoDismiss: 5,
        type: "error"
      });
      return;
    }
    dispatch(updateDynamicSwitchGroup(workingCodexVersionId, dynamicSwitchGroup.dynamicSwitchGroupId, updatedDynamicSwitchGroup));
    setSubmitting(false);
  }

  const submitDynamicSwitchGroup = (values, { setSubmitting }) => {
    if (!dynamicSwitchGroup) {
      setSubmitting(false);
      return;
    }
    const updatedDynamicSwitchGroup = cloneDeep(dynamicSwitchGroup);
    updatedDynamicSwitchGroup.dynamicSwitchGroupName = values.dynamicSwitchGroupName;
    updatedDynamicSwitchGroup.isActive = values.isActive;
    updatedDynamicSwitchGroup.pharmacistNotes = values.pharmacistNotes;
    handleSaveEditMode(updatedDynamicSwitchGroup, setSubmitting);
  }

  const setIsActiveValue = (values, e, handleBlur) => {
    values.isActive = !values.isActive;
    handleBlur(e);
  };

  const isFieldRequired = (key) => {
    return DRUG_GROUP_SCHEMA.fields[key]
      ? DRUG_GROUP_SCHEMA.fields[key].exclusiveTests.required
      : false;
  }

  const renderCell = (fieldName, className) => {
    if (!dynamicSwitchGroup) return null;
    const isUpdated = oldDynamicSwitchGroup && !dynamicSwitchGroup.isDeleted ? true : false;
    const newValue = dynamicSwitchGroup && dynamicSwitchGroup[fieldName] ? dynamicSwitchGroup[fieldName] : null;
    const oldValue = oldDynamicSwitchGroup && oldDynamicSwitchGroup[fieldName] ? oldDynamicSwitchGroup[fieldName] : null;
    return <DescriptionDeltaField
      key={dynamicSwitchGroup.dynamicSwitchGroupId + fieldName}
      oldField={oldValue}
      newField={newValue}
      className={className}
      isUpdated={isUpdated} />
  }

  const renderBoolCell = (fieldName, className) => {
    if (!dynamicSwitchGroup) return null;
    const isUpdated = oldDynamicSwitchGroup && !dynamicSwitchGroup.isDeleted ? true : false;
    const newValue = dynamicSwitchGroup ? dynamicSwitchGroup[fieldName] : null;
    const oldValue = oldDynamicSwitchGroup ? oldDynamicSwitchGroup[fieldName] : null;
    return <BooleanDeltaField
      key={dynamicSwitchGroup.dynamicSwitchGroupId + fieldName}
      oldField={oldValue}
      newField={newValue}
      className={className}
      isUpdated={isUpdated} />
  }

  const getIsDsgChanged = () => {
    if (!showDelta || !oldDynamicSwitchGroup)
      return false;
    let dsgModifiedFields = Object.keys(DRUG_GROUP_SCHEMA.fields);
    for (let i = 0; i < dsgModifiedFields.length; i++) {
      if (dynamicSwitchGroup && oldDynamicSwitchGroup &&
        dynamicSwitchGroup[dsgModifiedFields[i]] != oldDynamicSwitchGroup[dsgModifiedFields[i]] &&
        (dynamicSwitchGroup[dsgModifiedFields[i]] || oldDynamicSwitchGroup[dsgModifiedFields[i]])) {
        return true;
      }
    }
    return false;
  }

  const getClassName = () => {
    const isChanged = getIsDsgChanged();
    const isOldDrugPresent = oldDynamicSwitchGroup ? true : false
    const isNewDrugDeleted = dynamicSwitchGroup && dynamicSwitchGroup.isDeleted ? true : false;
    return getDeltaBoxClassName(showDelta, isChanged, isOldDrugPresent, isNewDrugDeleted)
  }

  const renderIcon = () => {
    const className = getClassName();
    return <DrugDeltaIcon
      showDelta={true}
      className={className}
    />
  }

  const refreshList = () => {
    history.push("/cat/codex/dsg");
  }

  // Early return if dynamicSwitchGroup is null
  if (!dynamicSwitchGroup) {
    return null;
  }

  const isDsgNameRequired = isFieldRequired("dynamicSwitchGroupName");
  const isPharmacistsNotesRequired = isFieldRequired("pharmacistNotes")
  let mainClass = getClassName();
  const searchParams = new URLSearchParams(window.location.search);
  const enableEditing = !searchParams.has('showDisplayCodexBar');
  mainClass = mainClass ? mainClass + "-card-border" : ""
  return (
      <Row>
        <Col md={12}>
          <Formik
            initialValues={{
              dynamicSwitchGroupId: dynamicSwitchGroup.dynamicSwitchGroupId || "",
              dynamicSwitchGroupName: dynamicSwitchGroup.dynamicSwitchGroupName || "",
              dynamicSwitchGroupClassName: dynamicSwitchGroup.dynamicSwitchGroupClassName || "",
              dynamicSwitchGroupSubClassName: dynamicSwitchGroup.dynamicSwitchGroupSubClassName || "",
              additionalClassName: dynamicSwitchGroup.additionalClassName || "",
              pharmacistNotes: dynamicSwitchGroup.pharmacistNotes || "",
              isActive: dynamicSwitchGroup.isActive || false,
            }}
            validationSchema={DRUG_GROUP_SCHEMA}
            onSubmit={submitDynamicSwitchGroup}
            onReset={handleCancelEditMode}
            innerRef={dsgEditFormRef}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit, handleReset }) => (
              <Form onSubmit={handleSubmit} onReset={handleReset}>
                <Row>
                  <Col md={12}>
                    <Card className={'drug-group-box ' + mainClass}>
                      <Card.Body className={`dynamic-switch-group-name ${!showDetails ? 'card-body-padding-override' : ''}`} 
                        style={{ borderColor: "#321323", borderRadius: 2, paddingTop: 5 }}>
                        <Card.Title className={!showDetails ? 'no-margin' : ''}>
                          {showDelta ? null : <EditDynamicSwitchGroupDetails />}
                          <div className='rm-xs-top-label pb-1'>
                            Dynamic Switch Group</div>
                          <span className={"drug-title"}>{renderIcon()}{dynamicSwitchGroup.dynamicSwitchGroupName}</span>
                          &nbsp;
                          <Badge className='drug-option-title-badge' bg={dynamicSwitchGroup.isActive ? 'success' : 'warning'} pill>{dynamicSwitchGroup.isActive ? 'Active' : 'Inactive'}</Badge>
                          &nbsp;
                          {
                            groupIsDirty
                              ? <Badge className='drug-option-title-badge' bg='danger' pill>Updating</Badge>
                              : null
                          }
                          <div className='pull-right drug-title-link-container'>
                            <EditExpandButtons
                              showDetails={showDetails}
                              toggleShowDetails={toggleShowDetails}
                              allowEditing={allowEditing && enableEditing}
                              isEditMode={isEditMode}
                              handleSubmit={handleSubmit}
                              handleReset={handleReset}
                              handleEnableEditMode={handleEnableEditMode}
                              onSuccessDelete={refreshList}
                              dsgId={dynamicSwitchGroup.dynamicSwitchGroupId}
                              name={dynamicSwitchGroup.dynamicSwitchGroupName}
                              codexId={workingCodexVersionId}
                            />
                          </div>
                        </Card.Title>
                        <div className='border-top mt-1'></div>
                        <Collapse in={showDetails}>
                          <div className="small-top-spacer">
                            <Row >
                              <Col md={12}>
                                {!isEditMode ?
                                  <>
                                    <div className='rm-small-top-label'>Group Name</div>
                                    {renderCell("dynamicSwitchGroupName", "")}
                                  </>
                                  :
                                  <Form.Group>
                                    <Form.Label className={isDsgNameRequired ? "rm-required-field" : ""} >
                                      Group Name
                                    </Form.Label>
                                    <Form.Control
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.dynamicSwitchGroupName}
                                      name="dynamicSwitchGroupName"
                                      id="drug-group-name"
                                      disabled={false}
                                    />
                                    <Form.Text id="drug-group-name-error-msg" className="text-small text-danger" >
                                      {errors.dynamicSwitchGroupName && touched.dynamicSwitchGroupName && errors.dynamicSwitchGroupName}
                                    </Form.Text>
                                  </Form.Group>
                                }
                              </Col>
                              <Col md={6}>
                                {!isEditMode ?
                                  <>
                                    <div className='rm-small-top-label'>Active</div>
                                    {renderBoolCell("isActive", "")}
                                  </>
                                  :
                                  <Form.Check
                                    inline
                                    onChange={(e) => setIsActiveValue(values, e, handleBlur)}
                                    onBlur={handleBlur}
                                    label={'Is Active'}
                                    name={`drug-group-is-active`}
                                    id={'drug-group-is-active'}
                                    // defaultChecked
                                    checked={values.isActive}
                                    disabled={!isEditMode}
                                  />
                                }
                              </Col>
                              <Col md={6}>
                                <div className='rm-small-top-label'>Additional Class Name</div>
                                {renderCell("additionalClassName", "")}
                              </Col>
                              <Col md={6}>
                                <div className='rm-small-top-label'>Group Sub Class Name</div>
                                {renderCell("dynamicSwitchGroupSubClassName", "")}
                              </Col>
                              <Col md={6}>
                                <div className='rm-small-top-label'>Group Class Name</div>
                                {renderCell("dynamicSwitchGroupClassName", "")}
                              </Col>
                            </Row>
                            <Row className="small-top-spacer">
                              <Col md={12}>
                                {isEditMode ?
                                  !dynamicSwitchGroup.pharmacistNotes && !showNotesTextArea ?
                                    <ToggleIconLinkButton
                                      shouldDisplayFirstTag={true}
                                      toggleButton={handleShowNotesTextArea}
                                      FirstIcon={MdNoteAdd}
                                      firstTag={"Add Note"}
                                      SecondIcon={null}
                                      secondTag={null}
                                    />
                                    :
                                    <Form.Group>
                                      <Form.Label className={isPharmacistsNotesRequired ? "rm-required-field" : ""} >
                                        Notes
                                      </Form.Label>
                                      <Form.Control
                                        as="textarea"
                                        rows={1}
                                        placeholder="Notes or comments about this drug"
                                        onChange={handleChange}
                                        value={values.pharmacistNotes}
                                        disabled={false}
                                        name="pharmacistNotes"
                                        id="drug-group-form-pharmacist-notes"
                                        className='rm-switch-note'
                                      />
                                      <Form.Text id="drug-group-form-pharmacist-notes-error-msg" className="text-small text-danger" >
                                        {errors.pharmacistNotes && touched.pharmacistNotes && errors.pharmacistNotes}
                                      </Form.Text>
                                    </Form.Group>
                                  :
                                  dynamicSwitchGroup.pharmacistNotes ?
                                    <>
                                      <div className='rm-small-top-label'>Notes</div>
                                      {renderCell("pharmacistNotes", "")}
                                    </>
                                    : <></>
                                }
                              </Col>
                            </Row>
                          </div>
                        </Collapse>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
  )
}

export default withRouter(DynamicSwitchGroupBox);
